import React, { useState } from 'react';
import ConfirmAction from '@/components/confirmaction';
import { Alert, Button, Empty, Form, Input, Pagination, Select, Skeleton, Tag, message } from 'antd';
import { ClockCircleOutlined, InfoCircleOutlined, ReloadOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import { Link, Navigate } from 'react-router-dom';
import FeaturePage from '@/components/featurepage';
import PullToRefresh from '@/components/pulltorefresh';
import UserIdentity from '@/components/useridentity';
import RecommendationPreferences from '@/components/recommendationpreferences';
import { deleteRecommendationFeedback, fetchMyRecommendationFeedback } from '@/apis/posts';
import usePrivateList from '@/utils/usePrivateList';
import usePrivateAction from '@/utils/usePrivateAction';
import useMobile from '@/utils/useMobile';
import { compactQueryParams, resourceIdRule } from '@/utils/queryParams';
import './index.scss';

const formatUpdatedAt = value => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
};

const MyFeedback = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const mobile = useMobile();
  const { busy, run } = usePrivateAction();
  const [form] = Form.useForm();
  const [removingId, setRemovingId] = useState(null);
  const { data, loading, error, load, isAuthenticated } = usePrivateList(fetchMyRecommendationFeedback, { ...filters, page, pageSize: 10 }, '推荐反馈加载失败');
  const hasFilters = Object.keys(filters).length > 0;
  const reset = () => {
    form.resetFields(); setFilters({}); setPage(1);
    if (!hasFilters && page === 1) void load().catch(() => {});
  };
  const remove = async item => {
    if (busy || loading) return;
    setRemovingId(item.id);
    try {
      await run(() => deleteRecommendationFeedback(item.id), () => {
        message.success('推荐反馈已撤销');
        if (data?.data.length === 1 && page > 1) setPage(current => current - 1);
        else void load().catch(() => {});
      });
    } finally { setRemovingId(null); }
  };
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="myFeedback personalWorkspace" title="推荐偏好" subtitle="调整推荐设置，管理你标记的感兴趣和已隐藏记录。">
    <RecommendationPreferences />
    <PullToRefresh enabled={mobile} disabled={loading || busy} onRefresh={load} successMessage="推荐反馈已刷新">
      <section className="featurePage-panel feedback-records" aria-labelledby="feedback-records-heading">
        <div className="feedback-records-heading">
          <div><h2 id="feedback-records-heading">我的反馈</h2><p>查看和调整你对推荐帖子的反馈。</p></div>
          <div className="feedback-records-summary">
            <span aria-live="polite">{loading ? '正在加载…' : error ? '加载失败' : `${hasFilters ? '筛选结果' : '共'} ${data?.pagination?.total ?? data?.data.length ?? 0} 条`}</span>
            {!mobile && <Button icon={<ReloadOutlined />} disabled={loading || busy} onClick={() => load().catch(() => {})}>刷新</Button>}
          </div>
        </div>
        <Form form={form} name="recommendation-feedback-filters" className="feedback-filter-form" layout="vertical" onFinish={values => {
          const next = compactQueryParams(values);
          if (page === 1 && JSON.stringify(next) === JSON.stringify(filters)) void load().catch(() => {});
          else { setFilters(next); setPage(1); }
        }}>
          <Form.Item name="action" label="反馈类型"><Select allowClear disabled={loading || busy} placeholder="全部反馈" options={[{ value: 'interested', label: '感兴趣' }, { value: 'dismissed', label: '已隐藏' }]} /></Form.Item>
          <Form.Item name="post_id" label="帖子编号" rules={[resourceIdRule('帖子编号')]}><Input disabled={loading || busy} maxLength={19} inputMode="numeric" placeholder="输入帖子编号" /></Form.Item>
          <div className="feedback-filter-actions"><Button type="primary" htmlType="submit" icon={<SearchOutlined />} disabled={loading || busy}>筛选</Button><Button disabled={loading || busy} onClick={reset}>重置</Button></div>
        </Form>
        <p className="feedback-hint"><InfoCircleOutlined aria-hidden="true" /><span>撤销反馈不会撤回申请或退出群聊，也不保证帖子重新进入推荐。</span></p>
        {loading ? <div className="feedback-loading"><Skeleton active /></div> : error ? <Alert type="error" showIcon title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <>
          {!data?.data.length && <div className="feedback-empty"><Empty description={hasFilters ? '暂无符合条件的推荐反馈' : '暂无推荐反馈'} /><p>{hasFilters ? '试试其他筛选条件，或查看全部反馈。' : '在推荐列表中标记感兴趣或隐藏帖子后，记录会显示在这里。'}</p>{hasFilters && <Button onClick={reset}>查看全部反馈</Button>}</div>}
          <div className="feedback-list">
            {data?.data.map(item => {
              const updatedAt = formatUpdatedAt(item.updated_at);
              return <article className="myFeedback-item" key={item.id}>
                <div className="myFeedback-item-heading"><h3>{item.post_context ? <Link to={`/posts/${item.post_id}`}>{item.post_context.title}</Link> : <span>帖子 {item.post_id} 当前不可用</span>}</h3><Tag color={item.action === 'interested' ? 'success' : 'default'}>{item.action === 'interested' ? '感兴趣' : '已隐藏'}</Tag></div>
                <div className="feedback-item-footer">
                  <div className="feedback-item-meta">
                    {item.post_context && <span><UserIdentity user={item.post_context.publisher} />{item.post_context.completed_at && <Tag color="cyan">活动已完成</Tag>}</span>}
                    {updatedAt && <span><ClockCircleOutlined aria-hidden="true" /><time dateTime={item.updated_at}>更新于 {updatedAt}</time></span>}
                  </div>
                  <ConfirmAction title="确认撤销这条推荐反馈？" disabled={loading || busy} onConfirm={() => remove(item)}><Button icon={<UndoOutlined />} disabled={loading || busy} loading={busy && removingId === item.id}>撤销反馈</Button></ConfirmAction>
                </div>
              </article>;
            })}
          </div>
          {data?.pagination?.total > 10 && <Pagination className="feedback-pagination" current={page} pageSize={10} total={data.pagination.total} disabled={busy} showSizeChanger={false} onChange={setPage} />}
        </>}
      </section>
    </PullToRefresh>
  </FeaturePage>;
};
export default MyFeedback;
